import * as React from "react";
import "./QuoteListPage.scss";
import Api from "./Api";
import Employee from "./Employee";
import SelectCtl from "./input/SelectCtl";
import Dialog from "./input/Dialog";
import DateRange from "./DateRange";
import { Deal } from "@testout/testout-commerce/models/hubspot/Deal";
import { Customer } from "@testout/testout-commerce/models/commerce/Customer";
import Segment from "@testout/testout-commerce/models/Segment";
import { Territory } from "@testout/testout-commerce/models/commerce/Territory";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { PagedList } from "@testout/testout-commerce/models/PagedList";
import { IQuote, Quote, QuoteStatus } from "@testout/testout-commerce/models/commerce/Quote";
import FilterCtl from "./input/FilterCtl";
import { SelectViewCtl, IEmployeeView2 } from "./SelectViewCtl";
import { getViewName } from "./SalesOrderListPage";

interface QuoteListPageProps { }

interface QuoteListPageState {
    errorMessage: string;
    searchInProgress: boolean;
    inProgress: string;
    quotes: Quote[];
    defaultView: IEmployeeView2
    view: IEmployeeView2;
    page: number;
    rowsPerPage: number;
    totalCount: number;
    dialogTitle?: string;
    dialogUrl?: string;
    filterDefaultItems: { [name: string]: ListItem };
    employees: ListItem[];
}

export default class QuoteListPage extends React.PureComponent<QuoteListPageProps, QuoteListPageState> {

    constructor(props: QuoteListPageProps) {
        super(props);
        const defaultView = {
            name: "My Territory Quotes Today",
            type: "Quote",
            employeeNumber: Employee.current?.employeeNumber ?? ""
        };
        this.state = {
            inProgress: "",
            defaultView: defaultView, view: { ...defaultView },
            errorMessage: "", searchInProgress: false, quotes: [], employees: [],
            page: 0, rowsPerPage: 25, totalCount: 0, filterDefaultItems: {}
        };
    }

    _searchRef = React.createRef<HTMLInputElement>();

    _rowsPerPageList: ListItem[] = [
        { name: "25 per page", value: "25" },
        { name: "50 per page", value: "50" },
        { name: "100 per page", value: "100" }
    ];

    async componentDidMount() {
        window.addEventListener("message", this.closeWindow);
        await this.initFilters();
        this.onRefreshClick();

        const divSearch = this._searchRef.current;
        if (divSearch)
            divSearch.focus();
    }

    componentWillUnmount() {
        window.removeEventListener("message", this.closeWindow);
    }

    closeWindow = () => {
        this.setState({ dialogTitle: "", dialogUrl: "" });
    }

    private async initFilters() {
        try {
            // Save the employee list for lookups
            let employees = await Api.get<ListItem[]>('/api/employees/list-items');
            this.setState({ employees: employees });

            const query = new URLSearchParams(window.location.search);
            let filterDefaultItems: { [name: string]: ListItem } = {};

            // Get the default salesperson
            let employeeNumber = Employee.current?.employeeNumber;
            filterDefaultItems["SalespersonCode"] = {
                name: "Me", value: employeeNumber,
                description: "The current user"
            };

            // Get the default territories
            const territories = await Api.get<Territory[]>(`/api/employees/${employeeNumber}/territories`);
            const territoryCodes: string | null = territories && territories.length ? territories.map(t => t.territoryCode).join(",") : null;
            if (territoryCodes)
                filterDefaultItems["TerritoryCode"] = {
                    name: "My Territory", value: territoryCodes,
                    description: "The current user's territories"
                };

            // Get the default customer
            const customerNumber = query.get("customerNumber");
            const customer: Customer | null = customerNumber ? await Api.get<Customer>(`/api/customers/${customerNumber}`) : null;
            if (customerNumber && customer)
                filterDefaultItems["CustomerNumber"] = {
                    name: customer.name ?? "Associated Customer", value: customerNumber,
                    description: "Selected customer"
                };

            // Get the default deal (if any)
            const dealId = query.get("dealId");
            const deal: Deal | null = dealId ? await Api.get<Deal>(`/api/deals/${dealId}`) : null;
            if (dealId && deal)
                filterDefaultItems["DealId"] = {
                    name: deal.dealname ?? "Associated Deal", value: dealId,
                    description: "Associated with deal from previous page"
                };

            // Set the default view
            let defaultView: IEmployeeView2;
            if (customerNumber && customer)
                defaultView = {
                    ...this.state.defaultView,
                    name: `Quotes for ${customer.name}`,
                    filters: { "CustomerNumber": [customerNumber] }
                };
            else if (dealId && deal)
                defaultView = {
                    ...this.state.defaultView,
                    name: `Quotes for ${deal.dealname}`,
                    filters: { "DealId": [dealId] }
                };
            else
                defaultView = {
                    ...this.state.defaultView,
                    name: "My Territory Quotes Today",
                    filters: territoryCodes
                        ? { "TerritoryCode": territoryCodes.split(','), "QuoteDate": ["TODAY"] }
                        : { "QuoteDate": ["TODAY"] }
                };

            this.setState({ filterDefaultItems: filterDefaultItems, defaultView: defaultView, view: { ...defaultView } });
        }
        catch (e: any) {
            this.setState({ errorMessage: e.message });
        }
    }

    onFilterSelect = async (name: string, value?: string): Promise<ListItem[]> => {
        let list: ListItem[];
        switch (name) {
            case "SalespersonCode":
                list = value
                    ? [...this.state.employees.filter(e => e.name.toLowerCase().includes(value.toLowerCase()) ||
                        e.value?.toLowerCase().includes(value.toLowerCase()) || e.description?.toLowerCase().includes(value.toLowerCase()))]
                    : [...this.state.employees];
                if (this.state.filterDefaultItems[name])
                    list.unshift(this.state.filterDefaultItems[name]);
                return list;
            case "TerritoryCode":
                list = value
                    ? await Api.get<ListItem[]>(`/api/territories/list-items?search=${encodeURIComponent(value)}`)
                    : await Api.get<ListItem[]>(`/api/territories/list-items`);
                if (this.state.filterDefaultItems[name])
                    list.unshift(this.state.filterDefaultItems[name]);
                return list;
            case "CustomerNumber":
                list = value
                    ? await Api.get<ListItem[]>(`/api/customers/list-items?search=${encodeURIComponent(value)}`)
                    : [];
                if (this.state.filterDefaultItems[name])
                    list.unshift(this.state.filterDefaultItems[name]);
                return list;
            case "DealId":
                return this.state.filterDefaultItems[name] ? [this.state.filterDefaultItems[name]] : [];
            case "QuoteDate":
                list = DateRange.items;
                return value ? list.filter(item => item.name.toLowerCase().includes(value.toLowerCase())) : list;
            case "ExpirationDate":
                list = DateRange.items;
                return value ? list.filter(item => item.name.toLowerCase().includes(value.toLowerCase())) : list;
            case "Segment":
                list = Segment.items;
                return value ? list.filter(item => item.name.toLowerCase().includes(value.toLowerCase())) : list;
            case "Status":
                list = (Object.values(QuoteStatus) as Array<keyof typeof QuoteStatus>)
                    .filter(v => typeof (v) === "string").map((status): ListItem => ({ name: status }));
                return value ? list.filter(item => item.name.toLowerCase().includes(value.toLowerCase())) : list;
            default:
                throw new Error(`Filter ${name} is not available.`);
        }
    }

    onFilterChange = (name: string, values: string[]) => {
        let expandedValues: string[] = [];

        // Expand any compound values (separated with comma)
        for (const value of values)
            if (value.includes(",")) {
                for (const subValue in value.split(","))
                    if (!expandedValues.includes(subValue))
                        expandedValues.push(subValue);
            }
            else if (!expandedValues.includes(value))
                expandedValues.push(value);

        let viewName = getViewName('Quotes', 'QuoteDate', name, values, expandedValues, this.state.view,
            this.state.filterDefaultItems["TerritoryCode"]?.value ?? "");

        this.setState({
            view: {
                ...this.state.view,
                name: viewName,
                filters: { ...this.state.view.filters, [name]: expandedValues }
            }
        }, this.onRefreshClick);
    };

    onFilterClear = () => this.setState({ view: { ...this.state.view, filters: {} } }, this.onRefreshClick);

    onSearchChange = (value: string) => this.setState({ view: { ...this.state.view, search: value } }, this.onRefreshClick);

    onViewChange = (view: IEmployeeView2) => this.setState({ view: { ...view } }, this.onRefreshClick);

    onPageChange = (value: number) => this.setState({ page: value }, this.onRefreshClick);

    onRowsPerPageChange = (value: string) => this.setState({ page: 0, rowsPerPage: parseInt(value) }, this.onRefreshClick);

    private _debounceTimer: number | undefined = undefined;
    onRefreshClick = async () => {
        // Limit refresh interval to minimum of 300ms so we don't overwhelm the server
        if (this._debounceTimer)
            window.clearTimeout(this._debounceTimer);
        this._debounceTimer = window.setTimeout(async () => {
            this._debounceTimer = undefined;

            const { view, page, rowsPerPage } = this.state;
            if ((view.search ?? "").length === 0 || (view.search ?? "").length >= 3) {

                // Retrieve the list of orders using the search and filter values
                this.setState({ searchInProgress: true, errorMessage: "" });
                try {
                    let list = await Api.get<PagedList<Quote>>(`/api/quotes/list?search=${encodeURIComponent(view.search ?? "")}&filters=${encodeURIComponent(JSON.stringify(view.filters ?? {}))}&page=${page}&size=${rowsPerPage}`);
                    this.setState({
                        quotes: list?.items?.map((quote: Quote) => new Quote(quote)) ?? [],
                        totalCount: list?.totalCount ?? 0
                    });
                }
                catch (error: any) {
                    this.setState({ errorMessage: `Error while retrieving the quote list: ${error.message}` });
                }
                finally {
                    this.setState({ searchInProgress: false });
                }
            }
        }, 300);
    }

    getPossiblePageNumbers = (): number[] => {
        let startPage = this.state.page - 5;
        if (startPage < 0)
            startPage = 0;
        let array = new Array<number>();
        for (let i = 0; i < 11; i++) {
            array.push(startPage + i);
            if ((startPage + i + 1) * this.state.rowsPerPage >= this.state.totalCount)
                break;
        }
        return array;
    }

    onCreateClick = () => {
        this.setState({ dialogTitle: "Create Quote", dialogUrl:"/Quote" })
    }

    onExportClick = async () => {
        try {
            const { view, totalCount } = this.state;
            this.setState({ inProgress: `Exporting ${totalCount.toLocaleString()} records...` });
            await Api.get<PagedList<IQuote>>(`/api/quotes/export?search=${encodeURIComponent(view.search ?? "")}&filters=${encodeURIComponent(JSON.stringify(view.filters ?? {}))}&page=-1`);
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while exporting list: ${error.message}` });
        }
        finally {
            this.setState({ inProgress: "" });
        }
    }

    render() {
        const { defaultView, view, page, rowsPerPage, filterDefaultItems, totalCount } = this.state;

        return <div className="quoteListPage">
            <div className="title">
                <div>
                    <SelectViewCtl defaultView={defaultView} view={view} onChange={this.onViewChange} />
                    <div className="actions">
                        <button className="primary" title="Create Customer" onClick={this.onCreateClick}>Create Quote</button>
                    </div>
                </div>
                <h6>{totalCount.toLocaleString()} records</h6>
            </div>

            <div className="filters">
                <FilterCtl name="SalespersonCode" label="Owner" values={view.filters?.["SalespersonCode"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="TerritoryCode" label="Territory" values={view.filters?.["TerritoryCode"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="Segment" label="Segment" values={view.filters?.["Segment"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="Status" label="Status" values={view.filters?.["Status"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="QuoteDate" label="Quote Date" values={view.filters?.["QuoteDate"]} multiSelect={false} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="ExpirationDate" label="Expires Date" values={view.filters?.["ExpirationDate"]} multiSelect={false} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="CustomerNumber" label="Customer" values={view.filters?.["CustomerNumber"]} multiSelect={true}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                {filterDefaultItems["DealId"] && <FilterCtl name="DealId" label="Deal" values={view.filters?.["DealId"]} multiSelect={false}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />}
                {Object.keys(view.filters ?? {}).find((name: string) => !!view.filters?.[name]?.length) &&
                    <button title="Clear All" className="clear-all" onClick={this.onFilterClear}>Clear All</button>}
            </div>

            <div className="searchDiv">
                <div className="search">
                    <input type="text" ref={this._searchRef}
                           placeholder="Search for quote number, PO number, or customer email"
                        value={view.search}
                        onChange={(e) => this.onSearchChange(e.target.value)} />
                    <button className={`search${this.state.searchInProgress ? " spinner" : ""}`}
                        title="Search/Refresh"
                            onClick={this.onRefreshClick}
                            disabled={this.state.searchInProgress}></button>
                </div>
                <div className="actions">
                    <a className="tertiary button" title="Export" href={`/api/quotes/export?search=${encodeURIComponent(view.search ?? "")}&filters=${encodeURIComponent(JSON.stringify(view.filters ?? "{}"))}`}>Export</a>
                </div>
            </div>

            {this.state.errorMessage &&
                <p className="error">{this.state.errorMessage}</p>}
            <div className="scrollableDiv">
                {this.state.searchInProgress &&
                    <div className="saving"><div><div>Searching...</div><div className="spinner"></div></div></div>}
                <table>
                    <thead>
                        <tr className={this.state.quotes.length === 0 ? "empty" : undefined}>
                            {/*<th className={this.thClassName("quoteNum")}><button onClick={() => this.sortButtonClick("quoteNum")}>QUOTE NUMBER</button></th>*/}
                            <th>QUOTE NUMBER</th>
                            <th>STATUS</th>
                            <th>QUOTE DATE</th>
                            <th>EXPIRES</th>
                            <th>SHIPPING CUSTOMER</th>
                            <th>SALESPERSON</th>
                            <th>TOTAL AMOUNT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.quotes.length === 0 && <tr className="empty" key="empty"><td colSpan={7}>No quotes match the current search text and filters.</td></tr>}
                        {this.state.quotes.map((quote: Quote) => <tr key={quote.quoteNumber}>
                            <td><button className="link" tabIndex={0} onClick={() => this.setState({
                                dialogTitle: `${quote.quoteNumber} - View/Edit Quote`,
                                dialogUrl: `/Quote/${quote.quoteNumber}`
                            })}>{quote.quoteNumber}</button></td>
                            <td>{QuoteStatus[quote.status]}</td>
                            <td>{quote.quoteDate.toLocaleDateString()}</td>
                            <td>{quote.expirationDate.toLocaleDateString()}</td>
                            <td>{quote.shippingCustomer.name ?? quote.shippingCustomer.address.companyName ?? quote.shippingCustomer.address.contactPerson.toString()}</td>
                            <td>{this.state.employees.find(e => e.value === quote.salespersonCode)?.name ?? quote.salespersonCode}</td>
                            <td>{(quote.totalAmount)?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
            <div className="pagination">
                <button className="pageButton" disabled={page === 0} onClick={() => this.onPageChange(page - 1)}><span style={{ fontSize: "large" }}>&#10094;</span>&nbsp;&nbsp;Prev</button>
                {this.getPossiblePageNumbers().map((i: number) =>
                    <button key={i} className={`pageButton ${i === this.state.page ? "currentPage" : ""}`}
                        onClick={() => this.onPageChange(i)}>{i + 1}</button>
                )}
                <button className="pageButton" disabled={(page + 1) * this.state.rowsPerPage >= totalCount}
                        onClick={() => this.onPageChange(page + 1)}>Next&nbsp;&nbsp;<span style={{ fontSize: "large" }}>&#10095;</span></button>
                <SelectCtl className="page-size" value={rowsPerPage.toString()} onChange={this.onRowsPerPageChange} hideItemValue={true} items={this._rowsPerPageList} />
            </div>

            {this.state.inProgress !== "" &&
                <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}

            <Dialog isOpen={!!this.state.dialogUrl} title={this.state.dialogTitle || ""} onClose={() => this.setState({ dialogTitle: "", dialogUrl: "" })}>
                <iframe src={this.state.dialogUrl} title="dialog" frameBorder="none" className="quote"></iframe>
            </Dialog>

        </div>;
    }
}